import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {Backdrop, Button, Step, StepLabel, Stepper} from "@mui/material";
import { getDatabase, ref, push, set } from "firebase/database";
import { auth } from "../../utils/firebase";
import LeagueDetails from "./LeagueDetails";
import PlayerSetup from "./PlayerSetup";
import HandicapSetup from "./HandicapSetup";
import PayoutSetup from "./PayoutSetup";
import MatchSetup from "./MatchSetup";
import ReviewConfirm from "./ReviewConfirm";
import BillingSetup from "./BillingSetup";
import { DotLottieReact } from '@lottiefiles/dotlottie-react';

const steps = [
    "League Details",
    "Add Players",
    "Handicap Setup",
    "Payout Setup",
    "Match Setup",
    "Billing Setup",
    "Review & Confirm",
    "Creating League"
];

const LeagueCreationWizard = () => {
    const [loadingState, setLoadingState] = useState("loading");
    const navigate = useNavigate();
    const [activeStep, setActiveStep] = useState(0);
    const [formData, setFormData] = useState({
        leagueName: "",
        leagueLocation: "",
        players: [],
        handicaps: {},
        flightName: "",
        flightRange: { start: "", end: "" },
        payoutName: "",
        payoutGroups: [],
        otherPayouts: [],
        matches: []
    });

    useEffect(() => {
        if (activeStep === steps.length - 1) {
            handleFinish();
        }
    }, [activeStep]);

    const handleNext = () => {
        setActiveStep((prevStep) => prevStep + 1);
    };

    const handleBack = () => {
        setActiveStep((prevStep) => prevStep - 1);
    };

    const handleFinish = async () => {
        console.log("handleFinish triggered");
        const db = getDatabase();
        const userId = auth.currentUser?.uid;
        if (!userId) {
            console.error("User not authenticated");
            return;
        }

        const leagueRef = push(ref(db, "leagues"));
        const leagueId = leagueRef.key;

        const generateId = () => push(ref(db, "temp")).key;

        const playersWithIds = formData.players.reduce((acc, player) => {
            const playerId = generateId();
            acc[player] = playerId;
            return acc;
        }, {});

        const finalData = {
            name: formData.leagueName,
            parentGroup: formData.leagueLocation,
            admin: userId,
            settings: {
                timeZone: formData.timeZone,
                language: formData.locale
            },
            players: Object.keys(playersWithIds).reduce((acc, playerName) => {
                const playerId = playersWithIds[playerName];
                acc[playerId] = { name: playerName, startDate: Date.now(), status: "active" };
                return acc;
            }, {}),
            handicaps: {
                [generateId()]: {
                    name: formData.handicapSetName,
                    startDate: formData.startDate ? new Date(formData.startDate).getTime()/1000 : null,
                    endDate: formData.endDate ? new Date(formData.endDate).getTime()/1000 : null,
                    playerHandicaps: Object.keys(formData.handicaps).reduce((acc, playerName) => {
                        const playerId = playersWithIds[playerName] || generateId();
                        acc[playerId] = formData.handicaps[playerName];
                        return acc;
                    }, {}),
                    flights: formData.flights.reduce((acc, flight) => {
                        acc[generateId()] = { name: flight.name, start: flight.min, end: flight.max };
                        return acc;
                    }, {})
                }
            },
            payouts: {
                [generateId()]: {
                    name: formData.payoutSetName,
                    startDate: formData.payoutStartDate ? new Date(formData.payoutStartDate).getTime()/1000 : null,
                    endDate: formData.payoutEndDate ? new Date(formData.payoutEndDate).getTime()/1000 : null,
                    mainPayouts: formData.payoutGroups.reduce((acc, group) => {
                        acc[generateId()] = {
                            name: group.name,
                            payouts: group.items.map(item => ({
                                ...item,
                                tieBehavior: item.tieBehavior || "split"
                            }))
                        };
                        return acc;
                    }, {}),
                    otherPayouts: formData.otherPayouts.map(payout => ({
                        name: payout.name,
                        amount: payout.amount,
                        holeBound: payout.holeBound || false
                    }))
                }
            },
            matches: formData.matches.reduce((acc, match) => {
                acc[generateId()] = {
                    dateTime: match.date ? new Date(match.date).getTime()/1000 : null,
                    finalized: false,
                    scores: {}
                };
                return acc;
            }, {})
        };

        try {
            const startTime = Date.now();
            await set(leagueRef, finalData);
            await set(ref(db, `people/${userId}/leagues/${leagueId}`), {
                player: true,
                admin: true
            });
            const elapsed = Date.now() - startTime;
            setTimeout(() => setLoadingState("complete"), Math.max(5000 - elapsed, 0));
            setTimeout(() => navigate(`/app/league/${leagueId}/dashboard`), Math.max(5000 - elapsed + 1500, 1500));
        } catch (error) {
            console.error("Error saving league:", error);
        }
    };

    const renderStepContent = (step) => {
        switch (step) {
            case 0:
                return <LeagueDetails formData={formData} setFormData={setFormData} />;
            case 1:
                return <PlayerSetup formData={formData} setFormData={setFormData} />;
            case 2:
                return <HandicapSetup formData={formData} setFormData={setFormData} players={formData.players || []} />;
            case 3:
                return <PayoutSetup formData={formData} setFormData={setFormData} />;
            case 4:
                return <MatchSetup formData={formData} setFormData={setFormData} />;
            case 5:
                return <BillingSetup formData={formData} />;
            case 6:
                return <ReviewConfirm data={formData} onBack={handleBack} />;
            case 7:
                return renderLoading();
            default:
                return <div>Unknown Step</div>;
        }
    };

    const renderLoading = () => {
        return (
            <Backdrop
                sx={(theme) => ({ color: '#fff', zIndex: theme.zIndex.drawer + 1 })}
                open={true}
            >
                {loadingState === "loading" ? (
                    <DotLottieReact
                        src="https://lottie.host/43a39854-9631-4f02-8b8c-c1f09e7e87af/dMcenwmL7W.lottie"
                        loop
                        autoplay
                    />
                ) : (
                    <DotLottieReact
                        src="https://lottie.host/97564f48-eb7e-4f63-b136-3e02ce637244/kpRH1cxdSf.lottie"
                        autoplay
                    />
                )}
            </Backdrop>
        );
    };

    return (
        <div className="container" style={{width:'700px'}}>
            <Stepper activeStep={activeStep} alternativeLabel>
                {steps.map((label, index) => (
                    <Step key={index}>
                        <StepLabel>{label}</StepLabel>
                    </Step>
                ))}
            </Stepper>
            <div className="wizard-content">{renderStepContent(activeStep)}</div>
            <div className="wizard-navigation" style={{display: "flex", justifyContent: "space-between"}}>
                <Button variant="text" disabled={activeStep === 0} onClick={handleBack}>Back</Button>
                {activeStep === steps.length - 2 ? (
                    <Button onClick={handleNext} variant="text">Create</Button>
                ) : activeStep < steps.length - 2 ? (
                    <Button onClick={handleNext} variant="text">Next</Button>
                ) : null}
            </div>
        </div>
    );
};

export default LeagueCreationWizard;